// Copyright ©️ 2026 eVolve MEP, LLC

import { useActionState } from 'react';

import type { PartCategory } from '@evolvemep/api-ts';

import type { WithOptional } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';

export type CategoryPath = readonly WithOptional<PartCategory, 'partCount'>[];

/** Builds the `CategoryPath` to reach a given `category`. */
const buildCategoryPath = (category: CategoryPath[number] | null | undefined): CategoryPath => {
  if (isNil(category)) return [];
  const { idPath, path: namePath } = category;
  return idPath.map((partCategoryId, i) => ({
    partCategoryId,
    partCatalogId: category.partCatalogId,
    parentPartCategoryId: idPath[i - 1],
    name: namePath[i] ?? '',
    hasSubCategories: i < idPath.length - 1 ? true : category.hasSubCategories,
    path: namePath.slice(0, i + 1),
    idPath: idPath.slice(0, i + 1),
    editable: i < idPath.length - 1 ? false : category.editable,
    partCount: i < idPath.length - 1 ? undefined : category.partCount,
  }));
};

/** Used to track the selected Category (and the path to get there) in a Part Catalog */
export const useCategoryPath = (
  /** If provided, will use this category to build the default `CategoryPath` */
  defaultCategory?: CategoryPath[number] | null,
) =>
  useActionState<CategoryPath, CategoryPath[number] | null>(
    (_, category) => buildCategoryPath(category),
    buildCategoryPath(defaultCategory),
  );
